import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, PenLine, FileSignature, Clock3, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import API from "@/lib/api";
import useLiveRefresh from "@/lib/useLiveRefresh";
import ConsultancyModal from "@/components/ConsultancyModal";

const ICONS = { astrology: Star, graphology: PenLine, signature: FileSignature };

const REQUIREMENTS = {
  astrology: "You will share: Name, Date of Birth, Time of Birth, Place of Birth, Phone, Email",
  graphology: "You will share: Name, Phone, Email + upload 2 pages of legible handwriting (~200 words, PDF or JPG)",
  signature: "You will share: Name, Phone, Email + upload your signature (PDF or JPG)",
};

const ServiceCard = ({ service, onBook }) => {
  const Icon = ICONS[service.id] || Star;
  return (
    <article data-testid={`consultancy-card-${service.id}`}
      className="group bg-[#FAF6F0] rounded-3xl p-7 soft-shadow border border-[rgba(45,90,71,0.1)] transition-transform duration-300 hover:-translate-y-1.5 flex flex-col">
      <div className="w-12 h-12 rounded-2xl bg-[#E8F3EE] flex items-center justify-center">
        <Icon className="text-[#2D5A47]" size={22} />
      </div>
      <h3 className="font-display text-xl sm:text-2xl font-bold text-[#2D5A47] mt-4">{service.name}</h3>
      <p className="mt-2 text-sm text-[#5A6E63] leading-relaxed flex-1">{service.description}</p>
      <p className="mt-3 text-xs text-[#5A6E63]/80 leading-relaxed">{REQUIREMENTS[service.id]}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="text-2xl font-bold text-[#1E2A24]">
          &#8377;{service.price}
          <span className="text-xs font-normal text-[#5A6E63]"> /{service.duration}</span>
        </span>
        <Button data-testid={`consultancy-book-btn-${service.id}`} onClick={() => onBook(service)}
          className="rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7] px-6 transition-transform duration-200 hover:scale-105">
          Book Now
        </Button>
      </div>
    </article>
  );
};

export default function Consultancy() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useLiveRefresh(() => {
    API.get("/consultancy/services")
      .then((res) => setServices(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  });

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="consultancy-page">
      <header className="border-b border-[rgba(45,90,71,0.15)] bg-[#FDFBF7]/80 backdrop-blur-md sticky top-0 z-40">
        <nav className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/" data-testid="consultancy-logo" className="flex items-center">
            <img src="/logo.jpeg" alt="Binfinite Mind" className="h-11 mix-blend-multiply" />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="consultancy-back-home">
              <Button variant="ghost" size="sm" className="rounded-full text-[#5A6E63]">Home</Button>
            </Link>
            <Link to="/dashboard" data-testid="consultancy-nav-dashboard">
              <Button variant="outline" size="sm" className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
                My Sessions
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      <section className="relative overflow-hidden grain-overlay pt-16 pb-12 sm:pt-20">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#E8F3EE] blur-3xl opacity-70" />
        <div className="absolute top-40 -left-32 w-80 h-80 rounded-full bg-[#F9EBEA] blur-3xl opacity-70" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#D98E32] font-semibold" data-testid="consultancy-eyebrow">
            One-on-One Consultancy
          </p>
          <h1 data-testid="consultancy-heading"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2D5A47] mt-4">
            Personal guidance, <span className="italic text-[#D98E32]">just for you</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-[#5A6E63] max-w-2xl mx-auto leading-relaxed" data-testid="consultancy-subtext">
            Book a private consultation with our experts. Choose a time slot that suits you,
            complete the payment, and within 48 hours our team will connect with you.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-xs text-[#5A6E63]">
            <span className="flex items-center gap-1.5 bg-[#E8F3EE] rounded-full px-4 py-2">
              <Clock3 size={13} className="text-[#2D5A47]" /> Morning: 8 AM - 12 PM
            </span>
            <span className="flex items-center gap-1.5 bg-[#E8F3EE] rounded-full px-4 py-2">
              <Clock3 size={13} className="text-[#2D5A47]" /> Evening: 6 PM - 10 PM
            </span>
            <span className="flex items-center gap-1.5 bg-[#F9EBEA] rounded-full px-4 py-2">
              <PhoneCall size={13} className="text-[#D98E32]" /> Team connects within 48 hours
            </span>
          </div>
        </div>
      </section>

      <section className="pb-16 sm:pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? (
            <p className="text-center text-[#5A6E63] mt-8" data-testid="consultancy-loading">Loading services...</p>
          ) : services.length === 0 ? (
            <p className="text-center text-[#5A6E63] mt-8" data-testid="consultancy-empty">Consultancy services coming soon.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => (
                <ServiceCard key={s.id} service={s} onBook={setSelected} />
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="py-10 bg-[#2D5A47] text-center">
        <div className="inline-flex items-center bg-[#FDFBF7] rounded-full px-6 py-2">
          <img src="/logo.jpeg" alt="Binfinite Mind" className="h-8" />
        </div>
        <p className="mt-3 text-sm text-[#E8F3EE] italic font-display" data-testid="consultancy-footer-text">
          "Live a worry free life"
        </p>
      </footer>

      <ConsultancyModal service={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
